import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api';
import { useAuth } from '../AuthContext';
import { useTheme } from '../contexts/ThemeContext';

const AY_ADI = ['Ocak','Şubat','Mart','Nisan','Mayıs','Haziran','Temmuz','Ağustos','Eylül','Ekim','Kasım','Aralık'];
const TL = (v: number) => `₺${Math.abs(v).toLocaleString('tr-TR', { maximumFractionDigits: 0 })}`;

const SUBE_RENK = ['#6366F1','#10B981','#F59E0B','#EC4899','#3B82F6','#8B5CF6'];

const GELIR = {
  kredi_yukleme: { renk: '#6366F1', label: 'Öğrenci Kredisi' },
  milonga:       { renk: '#EC4899', label: 'Milonga' },
  pratik:        { renk: '#10B981', label: 'Pratik' },
  kiralama:      { renk: '#F59E0B', label: 'Kiralama' },
  diger:         { renk: '#94A3B8', label: 'Diğer Gelir' },
};
const GELIR_KEYS = Object.keys(GELIR) as (keyof typeof GELIR)[];

export default function SuperAdminDashboard() {
  const { subeGir } = useAuth();
  const navigate = useNavigate();
  const { isDark } = useTheme();
  const card        = isDark ? '#1a1a1a' : 'white';
  const cardAlt     = isDark ? '#1e1e1e' : '#f8f8f8';
  const cardHover   = isDark ? '#252525' : '#fafafa';
  const border      = isDark ? '#2a2a2a' : '#eee';
  const borderSub   = isDark ? '#202020' : '#f0f0f0';
  const textPrimary = isDark ? '#e8e8e8' : '#1a1a1a';
  const textSec     = isDark ? '#999'    : '#888';
  const textMuted   = isDark ? '#666'    : '#aaa';
  const bugun = new Date();
  const [donem, setDonem] = useState({ yil: bugun.getFullYear(), ay: bugun.getMonth() + 1 });
  const [veri, setVeri] = useState<any>(null);
  const [yukleniyor, setYukleniyor] = useState(true);
  useEffect(() => {
    setYukleniyor(true);
    api.get(`/api/dashboard/super?yil=${donem.yil}&ay=${donem.ay}`)
      .then(r => setVeri(r.data))
      .catch(console.error)
      .finally(() => setYukleniyor(false));
  }, [donem]);

  const oncekiAy = () => setDonem(p => p.ay === 1 ? { yil: p.yil - 1, ay: 12 } : { ...p, ay: p.ay - 1 });
  const sonrakiAy = () => {
    const simdikiAy = new Date();
    const maxYil = simdikiAy.getFullYear(), maxAy = simdikiAy.getMonth() + 1;
    if (donem.yil > maxYil || (donem.yil === maxYil && donem.ay >= maxAy)) return;
    setDonem(p => p.ay === 12 ? { yil: p.yil + 1, ay: 1 } : { ...p, ay: p.ay + 1 });
  };

  const subeler: any[] = veri?.subeler || [];
  const toplamlar = veri?.toplamlar || {};
  const maxGelir  = Math.max(...subeler.map((s: any) => s.toplam_gelir), 1);

  if (yukleniyor) return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '60vh', color: textMuted, fontSize: 14 }}>
      Yükleniyor...
    </div>
  );

  return (
    <div style={{ padding: '24px 28px', maxWidth: 1280, background: isDark ? '#111111' : '#F8F9FB', minHeight: '100vh' }}>

      {/* ── Başlık ── */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 28 }}>
        <div>
          <h1 style={{ fontSize: 24, fontWeight: 700, color: textPrimary, margin: 0 }}>Genel Bakış</h1>
          <div style={{ fontSize: 13, color: textSec, marginTop: 4 }}>
            Tüm şubelerin aylık performans özeti
          </div>
        </div>

        {/* Dönem seçici */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 2, background: card, border: `1px solid ${border}`, borderRadius: 10, padding: '6px 4px' }}>
          <button onClick={oncekiAy} style={{ border:'none', background:'none', cursor:'pointer', padding:'4px 10px', fontSize:16, color: textSec, borderRadius:6, lineHeight:1 }}>‹</button>
          <span style={{ fontSize: 14, fontWeight: 600, color: textPrimary, minWidth: 120, textAlign: 'center' }}>
            {AY_ADI[donem.ay - 1]} {donem.yil}
          </span>
          <button onClick={sonrakiAy} style={{ border:'none', background:'none', cursor:'pointer', padding:'4px 10px', fontSize:16, color: textSec, borderRadius:6, lineHeight:1 }}>›</button>
        </div>
      </div>

      {/* ── KPI Özet Kartları ── */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 14, marginBottom: 28 }}>
        {[
          { label: 'Toplam Şube',    value: subeler.length,                             unit: 'aktif',    renk: '#6366F1', bg: isDark ? 'rgba(99,102,241,0.15)' : '#EEF2FF' },
          { label: 'Toplam Öğrenci', value: toplamlar.toplam_ogrenci || 0,              unit: 'öğrenci', renk: '#10B981', bg: isDark ? 'rgba(16,185,129,0.15)' : '#D1FAE5' },
          { label: 'Toplam Gelir',   value: TL(toplamlar.toplam_gelir || 0),            unit: 'bu dönem', renk: '#059669', bg: isDark ? 'rgba(5,150,105,0.15)' : '#ECFDF5' },
          { label: 'Net Kazanç',
            value: TL((toplamlar.toplam_gelir||0) - (toplamlar.toplam_gider||0)),
            unit: (toplamlar.toplam_gelir||0) >= (toplamlar.toplam_gider||0) ? 'kârlı' : 'zararlı',
            renk: (toplamlar.toplam_gelir||0) >= (toplamlar.toplam_gider||0) ? '#065F46' : '#991B1B',
            bg:   (toplamlar.toplam_gelir||0) >= (toplamlar.toplam_gider||0) ? (isDark ? 'rgba(16,185,129,0.15)' : '#D1FAE5') : (isDark ? 'rgba(228,75,74,0.12)' : '#FEE2E2') },
        ].map((k, i) => (
          <div key={i} style={{ background: card, border: `1px solid ${border}`, borderRadius: 12, padding: '18px 20px' }}>
            <div style={{ fontSize: 11, fontWeight: 600, color: textMuted, letterSpacing: 0.6, textTransform: 'uppercase', marginBottom: 10 }}>{k.label}</div>
            <div style={{ fontSize: 26, fontWeight: 700, color: textPrimary, marginBottom: 6 }}>{k.value}</div>
            <span style={{ background: k.bg, color: k.renk, borderRadius: 20, padding: '2px 10px', fontSize: 11, fontWeight: 600 }}>{k.unit}</span>
          </div>
        ))}
      </div>

      {/* ── Şube Kartları ── */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))', gap: 16, marginBottom: 32 }}>
        {subeler.map((sube: any, idx: number) => {
          const renk = SUBE_RENK[idx % SUBE_RENK.length];
          const giderPct = sube.toplam_gelir > 0 ? Math.min((sube.toplam_gider / sube.toplam_gelir) * 100, 100) : 0;
          const net = sube.net;

          return (
            <div key={sube.id} style={{
              background: card, border: `1px solid ${border}`, borderRadius: 14,
              overflow: 'hidden', display: 'flex', flexDirection: 'column',
              boxShadow: '0 1px 3px rgba(0,0,0,.06)',
            }}>
              {/* Üst şerit */}
              <div style={{ height: 4, background: renk }} />

              <div style={{ padding: '18px 20px 20px', flex: 1, display: 'flex', flexDirection: 'column', gap: 14 }}>
                {/* Başlık */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <div>
                    <div style={{ fontSize: 15, fontWeight: 700, color: textPrimary }}>{sube.ad}</div>
                    {sube.adres && <div style={{ fontSize: 11, color: textMuted, marginTop: 2 }}>{sube.adres}</div>}
                  </div>
                  <span style={{ background: renk + '18', color: renk, borderRadius: 20, padding: '3px 10px', fontSize: 11, fontWeight: 700, flexShrink: 0 }}>
                    Aktif
                  </span>
                </div>

                {/* İstatistikler */}
                <div style={{ display: 'flex', gap: 16 }}>
                  {[
                    { label: 'Öğrenci', value: sube.ogrenci_sayisi },
                    { label: 'Ders',    value: sube.ders_sayisi },
                    { label: 'Katılım', value: `%${sube.katilim_orani}` },
                  ].map((s, i) => (
                    <div key={i} style={{ flex: 1, textAlign: 'center', background: cardAlt, borderRadius: 8, padding: '10px 8px' }}>
                      <div style={{ fontSize: 20, fontWeight: 700, color: textPrimary }}>{s.value}</div>
                      <div style={{ fontSize: 10, color: textMuted, fontWeight: 500, marginTop: 2 }}>{s.label}</div>
                    </div>
                  ))}
                </div>

                {/* Gelir/Gider stacked mini bar */}
                <div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: textMuted, marginBottom: 6 }}>
                    <span>Gelir kırılımı</span>
                    <span>{TL(sube.toplam_gelir)}</span>
                  </div>
                  <div style={{ height: 8, background: isDark ? '#333' : '#F3F4F6', borderRadius: 4, overflow: 'hidden', display: 'flex', gap: 1 }}>
                    {GELIR_KEYS.map(k => {
                      const v = sube.gelir_dagilim[k] || 0;
                      const pct = sube.toplam_gelir > 0 ? (v / sube.toplam_gelir) * 100 : 0;
                      if (pct < 1) return null;
                      return <div key={k} style={{ width: `${pct}%`, background: GELIR[k].renk, transition: 'width .4s' }} />;
                    })}
                  </div>
                </div>

                {/* Gider bar */}
                <div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: textMuted, marginBottom: 6 }}>
                    <span>Gider</span>
                    <span style={{ color: '#EF4444' }}>{TL(sube.toplam_gider)}</span>
                  </div>
                  <div style={{ height: 6, background: isDark ? '#333' : '#F3F4F6', borderRadius: 4, overflow: 'hidden' }}>
                    <div style={{ height: '100%', width: `${giderPct}%`, background: '#EF4444', borderRadius: 4, transition: 'width .4s' }} />
                  </div>
                </div>

                {/* Net */}
                <div style={{
                  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                  padding: '10px 14px', borderRadius: 8,
                  background: net >= 0 ? (isDark ? 'rgba(16,185,129,0.15)' : '#F0FDF4') : (isDark ? 'rgba(228,75,74,0.12)' : '#FFF1F2'),
                }}>
                  <span style={{ fontSize: 12, fontWeight: 600, color: net >= 0 ? '#065F46' : '#991B1B' }}>Net Kazanç</span>
                  <span style={{ fontSize: 18, fontWeight: 800, color: net >= 0 ? '#059669' : '#EF4444' }}>
                    {net >= 0 ? '+' : '-'}{TL(net)}
                  </span>
                </div>

                {/* Şubeye Gir */}
                <button
                  onClick={() => subeGir(sube.id, sube.ad)}
                  style={{
                    marginTop: 2, padding: '10px', borderRadius: 8,
                    border: `1.5px solid ${renk}`, background: card,
                    color: renk, fontSize: 13, fontWeight: 700, cursor: 'pointer',
                    transition: 'background .15s, color .15s',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                  }}
                  onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.background = renk; (e.currentTarget as HTMLButtonElement).style.color = 'white'; }}
                  onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.background = card; (e.currentTarget as HTMLButtonElement).style.color = renk; }}
                >
                  Şubeye Gir →
                </button>
              </div>
            </div>
          );
        })}

        {/* Şube ekle kartı */}
        <div
          onClick={() => navigate('/subeler')}
          style={{
            background: card, border: `2px dashed ${border}`, borderRadius: 14,
            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
            padding: 32, cursor: 'pointer', gap: 10, minHeight: 200,
            transition: 'border-color .15s',
          }}
          onMouseEnter={e => (e.currentTarget.style.borderColor = '#6366F1')}
          onMouseLeave={e => (e.currentTarget.style.borderColor = isDark ? '#2a2a2a' : '#E5E7EB')}
        >
          <div style={{ width: 44, height: 44, borderRadius: '50%', background: isDark ? 'rgba(99,102,241,0.15)' : '#EEF2FF', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 22 }}>+</div>
          <div style={{ fontSize: 13, fontWeight: 600, color: '#6366F1' }}>Şube Ekle / Yönet</div>
          <div style={{ fontSize: 11, color: textMuted, textAlign: 'center' }}>Şubeler sayfasına git</div>
        </div>
      </div>

      {/* ── Gelir Dağılımı Karşılaştırması ── */}
      <div style={{ background: card, border: `1px solid ${border}`, borderRadius: 14, padding: '22px 24px', marginBottom: 20 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
          <div>
            <div style={{ fontSize: 15, fontWeight: 700, color: textPrimary }}>Gelir Dağılımı</div>
            <div style={{ fontSize: 12, color: textMuted, marginTop: 2 }}>Şube bazında gelir kırılımı</div>
          </div>
          {/* Legend */}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, justifyContent: 'flex-end' }}>
            {GELIR_KEYS.map(k => (
              <div key={k} style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 11, color: isDark ? '#ccc' : '#374151' }}>
                <div style={{ width: 10, height: 10, borderRadius: 2, background: GELIR[k].renk, flexShrink: 0 }} />
                {GELIR[k].label}
              </div>
            ))}
          </div>
        </div>

        {subeler.length === 0 ? (
          <div style={{ textAlign: 'center', color: textMuted, padding: 32, fontSize: 13 }}>Henüz şube verisi yok</div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            {subeler.map((sube: any, idx: number) => {
              const renk = SUBE_RENK[idx % SUBE_RENK.length];
              const barWidth = maxGelir > 0 ? (sube.toplam_gelir / maxGelir) * 100 : 0;
              return (
                <div key={sube.id}>
                  {/* Şube ismi + toplam */}
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <div style={{ width: 10, height: 10, borderRadius: '50%', background: renk, flexShrink: 0 }} />
                      <span style={{ fontSize: 13, fontWeight: 600, color: isDark ? '#ccc' : '#374151' }}>{sube.ad}</span>
                    </div>
                    <span style={{ fontSize: 13, fontWeight: 700, color: textPrimary }}>{TL(sube.toplam_gelir)}</span>
                  </div>

                  {/* Stacked bar */}
                  <div style={{ height: 22, background: isDark ? '#333' : '#F3F4F6', borderRadius: 6, overflow: 'hidden', display: 'flex', width: '100%' }}>
                    <div style={{ width: `${barWidth}%`, display: 'flex', gap: 1, overflow: 'hidden' }}>
                      {GELIR_KEYS.map(k => {
                        const v = sube.gelir_dagilim[k] || 0;
                        const pct = sube.toplam_gelir > 0 ? (v / sube.toplam_gelir) * 100 : 0;
                        if (pct < 1) return null;
                        return (
                          <div
                            key={k}
                            title={`${GELIR[k].label}: ${TL(v)}`}
                            style={{ width: `${pct}%`, background: GELIR[k].renk, minWidth: pct > 0 ? 3 : 0 }}
                          />
                        );
                      })}
                    </div>
                  </div>

                  {/* Alt kategori tutarları */}
                  <div style={{ display: 'flex', gap: 14, marginTop: 5, flexWrap: 'wrap' }}>
                    {GELIR_KEYS.map(k => {
                      const v = sube.gelir_dagilim[k] || 0;
                      if (v === 0) return null;
                      const pct = sube.toplam_gelir > 0 ? Math.round((v / sube.toplam_gelir) * 100) : 0;
                      return (
                        <div key={k} style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 11, color: textSec }}>
                          <div style={{ width: 8, height: 8, borderRadius: 2, background: GELIR[k].renk }} />
                          <span>{GELIR[k].label}: <strong style={{ color: isDark ? '#ccc' : '#374151' }}>{TL(v)}</strong></span>
                          <span style={{ color: '#D1D5DB' }}>%{pct}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* ── Gider Dağılımı ── */}
      <div style={{ background: card, border: `1px solid ${border}`, borderRadius: 14, padding: '22px 24px' }}>
        <div style={{ fontSize: 15, fontWeight: 700, color: textPrimary, marginBottom: 4 }}>Gider Dağılımı</div>
        <div style={{ fontSize: 12, color: textMuted, marginBottom: 20 }}>Şube bazında gider kırılımı</div>

        {subeler.length === 0 ? (
          <div style={{ textAlign: 'center', color: textMuted, padding: 32, fontSize: 13 }}>Henüz gider verisi yok</div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 16 }}>
            {subeler.map((sube: any, idx: number) => {
              const renk = SUBE_RENK[idx % SUBE_RENK.length];
              const gd = sube.gider_dagilim;
              const satirlar = [
                { label: 'Eğitmen Hakediş', tutar: gd.egitmen_odemeleri, renk: '#EF4444' },
                ...gd.detay.map((d: any) => ({ label: d.baslik, tutar: d.tutar, renk: '#F87171' })),
              ].filter(s => s.tutar > 0);
              const maxGiderTutar = Math.max(...satirlar.map(s => s.tutar), 1);

              return (
                <div key={sube.id} style={{ border: `1px solid ${borderSub}`, borderRadius: 10, overflow: 'hidden' }}>
                  <div style={{ background: renk, padding: '10px 14px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ fontSize: 12, fontWeight: 700, color: 'white' }}>{sube.ad}</span>
                    <span style={{ fontSize: 12, fontWeight: 700, color: 'white', opacity: 0.9 }}>{TL(sube.toplam_gider)}</span>
                  </div>
                  <div style={{ padding: '12px 14px', display: 'flex', flexDirection: 'column', gap: 10 }}>
                    {satirlar.length === 0 ? (
                      <div style={{ fontSize: 12, color: textMuted, textAlign: 'center', padding: '8px 0' }}>Bu dönemde gider yok</div>
                    ) : (
                      satirlar.map((s, i) => (
                        <div key={i}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, marginBottom: 4 }}>
                            <span style={{ color: isDark ? '#ccc' : '#374151', fontWeight: 500 }}>{s.label}</span>
                            <span style={{ color: '#EF4444', fontWeight: 600 }}>{TL(s.tutar)}</span>
                          </div>
                          <div style={{ height: 4, background: isDark ? 'rgba(228,75,74,0.12)' : '#FEE2E2', borderRadius: 2, overflow: 'hidden' }}>
                            <div style={{ height: '100%', width: `${(s.tutar / maxGiderTutar) * 100}%`, background: s.renk, borderRadius: 2, transition: 'width .4s' }} />
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

    </div>
  );
}
